/** Admin -> Audit log (eskiz 26): kim, qachon, nimani o'zgartirdi. Faqat owner, faqat o'qish. */
import { rememberPageSize, urlPageSize } from "@/lib/pageSize";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { formatDate, formatDateTime } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import { useUserOptions } from "@/features/drivers/api";
import { useAuditLog } from "@/features/admin/api";
import { AUDIT_MODELS, modelLabel, type AuditEntry } from "@/features/admin/types";
import { AdminLayout } from "./AdminLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FilterSelect, Input } from "@/components/ui/field";
import { Pagination, Table, Td, Th, Tr } from "@/components/ui/table";
import { EmptyState, ErrorState, PageLoading } from "@/components/ui/misc";
import { ContextMenu } from "@/components/ui/context-menu";
import { copyText } from "@/lib/clipboard";
import { auditMenu, auditObjectPath } from "@/features/admin/menu";
import { PersonLink } from "@/components/PersonLink";
import { usePersonNav } from "@/features/people/usePersonNav";


/** Model nomi tarjimasi: "accounts.user" -> admin:audit.model.user; noma'lum bo'lsa inglizcha label */
function useModelLabel() {
  const { t } = useTranslation("admin");
  return (model: string) => {
    const short = model ? (model.split(".").pop() ?? model) : "auth";
    return t(`audit.model.${short}`, { defaultValue: modelLabel(model) });
  };
}

export function AuditLogPage() {
  const { t } = useTranslation("admin");
  const label = useModelLabel();
  const me = useMe();
  const [params, setParams] = useSearchParams();
  const actor = params.get("actor") ?? "";
  const model = params.get("model") ?? "";
  const action = params.get("action") ?? "";
  const dateFrom = params.get("date_from") ?? "";
  const dateTo = params.get("date_to") ?? "";
  const q = params.get("q") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? 1));
  const pageSize = urlPageSize(params.get("page_size"), "audit", 50);
  const [selected, setSelected] = useState<AuditEntry | null>(null);

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== "page") next.delete("page");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const navigate = useNavigate();
  const { t: tm } = useTranslation("menu");
  const personNav = usePersonNav();
  const auditItems = (e: AuditEntry) =>
    auditMenu(e, { showDetails: () => setSelected(e), filterBy: (param, value) => setParam(param, value), navigate: (path) => navigate(path), copy: (text) => void copyText(text), profile: personNav }, tm);

  const isOwner = me.data?.role === "owner";
  const users = useUserOptions({}, isOwner);
  const log = useAuditLog({ actor: actor || undefined, model: model || undefined, action: action || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined, q: q || undefined, page, page_size: pageSize });
  const hasFilters = !!(actor || model || action || dateFrom || dateTo || q);

  if (!me.data) return <PageLoading />;
  if (!isOwner) {
    return (
      <AdminLayout title={t("audit.title")}>
        <EmptyState title={t("audit.ownerOnly")} description={t("audit.ownerOnlyDesc")} />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title={
        <span className="flex items-center gap-3">
          {t("audit.title")} <Badge tone="grey">{t("audit.readOnly")}</Badge>
        </span>
      }
      subtitle={t("audit.subtitle")}
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <FilterSelect value={actor} onChange={(e) => setParam("actor", e.target.value)} className="w-auto min-w-[170px] max-w-[280px]" aria-label={t("audit.col.user")}>
          <option value="">{t("audit.userAll")}</option>
          {users.data?.results.map((u) => (
            <option key={u.id} value={u.id}>
              {u.display_name}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect value={model} onChange={(e) => setParam("model", e.target.value)} className="w-auto min-w-[170px] max-w-[280px]" aria-label={t("audit.col.object")}>
          {AUDIT_MODELS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.value ? label(m.value) : t("audit.modelAll")}
            </option>
          ))}
        </FilterSelect>
        <Input value={action} onChange={(e) => setParam("action", e.target.value)} placeholder={t("audit.actionPlaceholder")} className="w-[170px]" aria-label={t("audit.col.action")} />
        <Input type="date" value={dateFrom} onChange={(e) => setParam("date_from", e.target.value)} className="w-[150px]" aria-label={t("audit.col.time")} />
        <span className="text-muted">–</span>
        <Input type="date" value={dateTo} onChange={(e) => setParam("date_to", e.target.value)} className="w-[150px]" aria-label={t("audit.col.time")} />
        <Input value={q} onChange={(e) => setParam("q", e.target.value)} placeholder={t("audit.searchPlaceholder")} className="w-[230px]" aria-label={t("common:actions.search")} />
        {hasFilters && (
          <Button variant="link" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            {t("common:actions.clearFilters")}
          </Button>
        )}
      </div>

      <div className="relative">
        {log.isPending ? (
          <PageLoading />
        ) : log.isError ? (
          <ErrorState message={log.error.message} onRetry={() => log.refetch()} />
        ) : log.data.results.length === 0 ? (
          <EmptyState title={t("audit.emptyTitle")} description={hasFilters ? t("audit.emptyFiltered") : t("audit.emptyDesc")} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t("audit.col.time")}</Th>
                  <Th>{t("audit.col.user")}</Th>
                  <Th>{t("audit.col.action")}</Th>
                  <Th>{t("audit.col.object")}</Th>
                  <Th>{t("audit.col.change")}</Th>
                  <Th>{t("audit.col.ip")}</Th>
                </tr>
              </thead>
              <tbody>
                {log.data.results.map((e) => (
                  <ContextMenu key={e.id} items={() => auditItems(e)}>
                    <Tr clickable onClick={() => setSelected(e)} className={selected?.id === e.id ? "bg-primary-soft/50" : undefined}>
                      <Td className="whitespace-nowrap text-muted">{formatDateTime(e.created_at)}</Td>
                      <Td className="whitespace-nowrap">{e.actor ? <PersonLink id={e.actor.id} name={e.actor.display_name} /> : e.actor_label || t("audit.system")}</Td>
                      <Td>
                        <ActionLabel action={e.action} />
                      </Td>
                      <Td>
                        <span className="text-muted">{label(e.model)}</span>
                        {e.object_repr && <> · </>}
                        {e.object_repr && (e.link ?? auditObjectPath(e.model, e.object_id)) ? (
                          <Link to={(e.link ?? auditObjectPath(e.model, e.object_id)) as string} onClick={(ev) => ev.stopPropagation()} className="hover:underline">
                            {e.object_repr}
                          </Link>
                        ) : (
                          e.object_repr
                        )}
                      </Td>
                      <Td className="max-w-[360px]">
                        <ChangeSummary changes={e.changes} />
                      </Td>
                      <Td className={e.action === "login_failed" ? "font-semibold text-danger" : "text-muted"}>{e.ip ?? "—"}</Td>
                    </Tr>
                  </ContextMenu>
                ))}
              </tbody>
            </Table>
            <Pagination
              page={page}
              pages={log.data.pages}
              count={log.data.count}
              pageSize={pageSize}
              onPage={(p) => setParam("page", String(p))}
              onPageSize={(s) => {
                rememberPageSize("audit", s);
                setParam("page_size", String(s));
              }} label={t("audit.events")} />
          </>
        )}

        {selected && <DetailDrawer entry={selected} onClose={() => setSelected(null)} />}
      </div>
    </AdminLayout>
  );
}

function ActionLabel({ action }: { action: string }) {
  const tone = action === "login_failed" || action === "block" || action === "archive" ? "red" : action === "create" || action === "login" ? "green" : "grey";
  return <Badge tone={tone}>{action.replace(/_/g, " ")}</Badge>;
}

function fmt(value: unknown, yes: string, no: string): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? yes : no;
  if (Array.isArray(value)) return value.map((v) => fmt(v, yes, no)).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  // "2026-11-02T19:28:00.181307+00:00" -> "Nov 3, 00:28" (mahalliy vaqt); "2026-12-07" -> "Dec 7, 2026"
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) return formatDateTime(value);
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDate(value);
  return String(value);
}

/** O'zgarishlar: {field: [old, new]} yoki hodisa tafsilotlari {key: value} */
function entries(changes: Record<string, unknown>, yes: string, no: string): { field: string; old: string | null; value: string }[] {
  return Object.entries(changes ?? {}).map(([field, v]) => {
    if (Array.isArray(v) && v.length === 2) return { field, old: fmt(v[0], yes, no), value: fmt(v[1], yes, no) };
    return { field, old: null, value: fmt(v, yes, no) };
  });
}

function ChangeSummary({ changes }: { changes: Record<string, unknown> }) {
  const { t } = useTranslation("admin");
  const list = entries(changes, t("audit.yes"), t("audit.no"));
  if (list.length === 0) return <span className="text-muted">—</span>;
  const shown = list.slice(0, 2);
  return (
    <div className="space-y-0.5 text-[12px]">
      {shown.map((c) => (
        <div key={c.field} className="truncate">
          <span className="text-muted">{c.field}:</span> {c.old !== null ? <>{c.old} → <span className="font-semibold">{c.value}</span></> : c.value}
        </div>
      ))}
      {list.length > 2 && <div className="text-[11px] text-muted">{t("audit.more", { n: list.length - 2 })}</div>}
    </div>
  );
}

/**
 * Tafsilot paneli (2026-11-03, foydalanuvchi: pastda bosilsa oyna tepada qolib ketardi): ekranga qotirilgan (`fixed`, body'ga portal),
 * qayerda bosilsa ham ko'rinadi, ichida o'zi aylanadi; Esc yopadi. Uzun qiymatlar (sana, id) qatorga sig'adi.
 */
function DetailDrawer({ entry, onClose }: { entry: AuditEntry; onClose: () => void }) {
  const { t } = useTranslation("admin");
  const label = useModelLabel();
  const list = entries(entry.changes, t("audit.yes"), t("audit.no"));
  const link = entry.link ?? auditObjectPath(entry.model, entry.object_id);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <aside
      role="dialog"
      aria-label={t("audit.showDetails")}
      className="pop-in fixed top-[68px] right-4 bottom-4 z-40 flex w-[min(460px,calc(100vw-2rem))] flex-col overflow-hidden rounded-card border border-border/60 bg-surface shadow-menu"
    >
      <div className="flex items-start justify-between gap-3 border-b border-line px-5 pt-4 pb-3">
        <div className="min-w-0">
          <div className="text-[15px] leading-snug font-semibold">
            {formatDateTime(entry.created_at)} · <ActionLabel action={entry.action} />
          </div>
          <div className="mt-1 text-[13px] break-words text-muted [overflow-wrap:anywhere]">
            {label(entry.model)}
            {entry.object_repr && (
              <>
                {" · "}
                {link ? (
                  <Link to={link} className="font-medium text-primary hover:underline">
                    {entry.object_repr}
                  </Link>
                ) : (
                  <span className="text-ink">{entry.object_repr}</span>
                )}
              </>
            )}
            {entry.object_id && <span className="text-faint"> · ID {entry.object_id}</span>}
          </div>
        </div>
        <button onClick={onClose} className="shrink-0 rounded-full p-1.5 text-muted hover:bg-fill hover:text-ink" aria-label={t("common:actions.close")}>
          <X size={16} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-3">
        <dl className="grid grid-cols-[112px_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[13px]">
          <dt className="text-muted">{t("audit.col.user")}</dt>
          <dd>{entry.actor ? <PersonLink id={entry.actor.id} name={entry.actor.display_name} /> : entry.actor_label || t("audit.system")}</dd>
          <dt className="text-muted">{t("audit.col.ip")}</dt>
          <dd>{entry.ip ?? "—"}</dd>
          <dt className="text-muted">{t("audit.requestId")}</dt>
          <dd className="font-mono text-[11px] [overflow-wrap:anywhere]">{entry.request_id || "—"}</dd>
        </dl>
        {list.length > 0 && (
          <table className="mt-4 w-full table-fixed text-[12px]">
            <colgroup>
              <col className="w-[30%]" />
              <col className="w-[35%]" />
              <col className="w-[35%]" />
            </colgroup>
            <thead>
              <tr className="text-left text-[11px] text-muted">
                <th className="py-1 font-semibold">{t("audit.field")}</th>
                <th className="py-1 font-semibold">{t("audit.old")}</th>
                <th className="py-1 font-semibold">{t("audit.new")}</th>
              </tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.field} className="border-t border-line align-top">
                  <td className="py-1.5 pr-2 text-muted [overflow-wrap:anywhere]">{c.field}</td>
                  <td className="py-1.5 pr-2 [overflow-wrap:anywhere]">{c.old ?? "—"}</td>
                  <td className="py-1.5 font-semibold [overflow-wrap:anywhere]">{c.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p className="mt-3 text-[11px] text-muted">{t("audit.immutable")}</p>
      </div>
    </aside>,
    document.body,
  );
}
